const FREE_ADDON_KEYS = {
  eligible: '_free_addon_eligible',
  parentKey: '_free_addon_parent_key',
  parentVariant: '_free_addon_parent_variant',
  link: '_free_addon_link',
  managed: '_free_addon',
};

const freeAddonConfig = window.freeAddonConfig || {};
const freeAddonVariantId = Number(freeAddonConfig.variantId || 0);

const state = {
  syncInFlight: false,
  syncQueued: false,
};

function randomAddonLink() {
  const rand = Math.floor(Math.random() * 1e9);
  return `fa_${Date.now()}_${rand}`;
}

function isTruthyProperty(value) {
  return value === true || value === 'true' || value === '1' || value === 1;
}

function getProperty(item, key) {
  if (!item || !item.properties) return '';
  return item.properties[key] ?? '';
}

async function getCart() {
  const res = await fetch('/cart.js', {
    headers: {
      Accept: 'application/json',
    },
  });

  if (!res.ok) {
    throw new Error('Unable to fetch cart');
  }

  return res.json();
}

async function addAddonForPaidLine({ parentLine, quantity, linkToken }) {
  const payload = {
    items: [
      {
        id: freeAddonVariantId,
        quantity,
        properties: {
          [FREE_ADDON_KEYS.managed]: '1',
          [FREE_ADDON_KEYS.link]: linkToken,
          [FREE_ADDON_KEYS.parentKey]: parentLine.key,
          [FREE_ADDON_KEYS.parentVariant]: String(parentLine.variant_id),
        },
      },
    ],
  };

  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    throw new Error('Unable to add free addon');
  }
}

async function changeLineQuantityByKey(key, quantity) {
  const res = await fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      id: key,
      quantity,
    }),
  });

  if (!res.ok) {
    throw new Error('Unable to change cart line');
  }
}

function buildGroups(cart) {
  const paidLines = cart.items.filter((item) => {
    if (item.variant_id === freeAddonVariantId) return false;
    return isTruthyProperty(getProperty(item, FREE_ADDON_KEYS.eligible));
  });

  const addonLines = cart.items.filter((item) => {
    if (item.variant_id !== freeAddonVariantId) return false;
    return isTruthyProperty(getProperty(item, FREE_ADDON_KEYS.managed));
  });

  return { paidLines, addonLines };
}

async function syncCart(cartOverride) {
  if (!freeAddonVariantId) return;

  const cart = cartOverride || (await getCart());
  const { paidLines, addonLines } = buildGroups(cart);
  const paidByKey = new Map(paidLines.map((line) => [line.key, line]));
  const addonByParent = new Map();

  for (const addon of addonLines) {
    const parentKey = String(getProperty(addon, FREE_ADDON_KEYS.parentKey) || '');
    if (!parentKey) continue;
    const existing = addonByParent.get(parentKey) || [];
    existing.push(addon);
    addonByParent.set(parentKey, existing);
  }

  let hasMutations = false;

  for (const paid of paidLines) {
    const linkedAddons = addonByParent.get(paid.key) || [];
    const requiredQty = paid.quantity;
    const primaryAddon = linkedAddons[0];
    const stableLinkToken =
      getProperty(primaryAddon, FREE_ADDON_KEYS.link) ||
      getProperty(paid, FREE_ADDON_KEYS.link) ||
      randomAddonLink();

    if (!primaryAddon && requiredQty > 0) {
      await addAddonForPaidLine({
        parentLine: paid,
        quantity: requiredQty,
        linkToken: stableLinkToken,
      });
      hasMutations = true;
    } else if (primaryAddon && primaryAddon.quantity !== requiredQty) {
      await changeLineQuantityByKey(primaryAddon.key, requiredQty);
      hasMutations = true;
    }

    if (linkedAddons.length > 1) {
      for (const duplicateAddon of linkedAddons.slice(1)) {
        await changeLineQuantityByKey(duplicateAddon.key, 0);
        hasMutations = true;
      }
    }
  }

  for (const addon of addonLines) {
    const parentKey = String(getProperty(addon, FREE_ADDON_KEYS.parentKey) || '');
    if (!parentKey || !paidByKey.has(parentKey)) {
      await changeLineQuantityByKey(addon.key, 0);
      hasMutations = true;
    }
  }

  if (hasMutations) {
    document.dispatchEvent(new CustomEvent('theme:update:cart'));
  }
}

async function runSync(cartOverride) {
  if (state.syncInFlight) {
    state.syncQueued = true;
    return;
  }

  state.syncInFlight = true;

  try {
    let nextCart = cartOverride;
    do {
      state.syncQueued = false;
      await syncCart(nextCart);
      nextCart = null;
    } while (state.syncQueued);
  } catch (error) {
    console.error('[Free Addon] sync failed', error);
  } finally {
    state.syncInFlight = false;
  }
}

function ensureFormLinkToken(formEl) {
  if (!formEl) return;

  const eligibleInput = formEl.querySelector(
    `input[name="properties[${FREE_ADDON_KEYS.eligible}]"]`
  );
  if (!eligibleInput) return;

  const linkInput = formEl.querySelector(
    `input[name="properties[${FREE_ADDON_KEYS.link}]"]`
  );
  if (!linkInput) return;

  linkInput.value = randomAddonLink();
}

document.addEventListener('submit', (event) => {
  const form = event.target.closest('form[action*="/cart/add"]');
  if (!form) return;
  ensureFormLinkToken(form);
});

document.addEventListener('theme:product:add', async (event) => {
  const cart = await event.detail.cartPromise;
  runSync(cart);
});

document.addEventListener('theme:line-item:change', (event) => {
  runSync(event.detail.cart);
});

document.addEventListener('theme:cart:update', async (event) => {
  const cart = await event.detail.cartPromise;
  runSync(cart);
});

document.addEventListener('DOMContentLoaded', () => {
  runSync();
});
